import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
import { isBookingPath } from "@/api/bookingPublic";
import { runCompanionBack } from "@/companion/backLink";
import { installChunkRecovery } from "@/components/pwa/chunkRecovery";
import { dropOfflineCopy } from "@/components/pwa/dropOfflineCopy";
import { PwaUpdatePrompt } from "@/components/pwa/PwaUpdatePrompt";
import { initDesktop, isMacShell } from "@/native/desktop";
import { DesktopBridge } from "@/native/DesktopBridge";
import { devForceMacShell } from "@/native/desktopDev";
import { exposeToNative, initEmbedded } from "@/native/embedded";
import "./index.css";

// Before anything lazy loads: a chunk an older build named reloads into the
// build the server has (components/pwa/chunkRecovery.ts).
installChunkRecovery();

// A web screen inside the iPhone app (?embed=ios, src/native/embedded.ts):
// read once, before the router sees the address.
const embedded = initEmbedded();
if (embedded) exposeToNative({ back: runCompanionBack });

// The Mac app's window (src/native/desktop.ts): read once, before the first
// render. A development build can play it in a browser with ?shell=mac
// (desktopDev.ts; the whole module is left out of a production build).
initDesktop(embedded, import.meta.env.DEV && devForceMacShell());
// No offline copy inside the Mac app: a saved build outlives the app and the
// server's deploys (the first Corvus Agent Platform showed the old app's saved
// dashboard, without its top row), so the window always loads the build the
// server has, like the iPhone's web screens. A worker an older build left is
// removed with its caches; the app also clears them for each new app build.
if (isMacShell()) dropOfflineCopy();

// A public booking page (/book/<slug>, /book/manage): a stranger's phone
// never registers the service worker or sees the update toast (spec §11.2).
// An operator whose installed worker serves index.html gets the same page.
const onBookingPage = isBookingPath(window.location.pathname);

const root = document.getElementById("root");
if (!root) throw new Error("#root not found in index.html");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
    {/* Mounted at the root (not inside App's auth gate) so the service
        worker registers on first load — installable before login — and
        the update toast can surface on any screen. Never inside the
        iPhone app or the Mac app: each loads the current build, so there
        is no worker to register and no update to hold. */}
    {!embedded && !isMacShell() && !onBookingPage && <PwaUpdatePrompt />}
    {isMacShell() && <DesktopBridge />}
  </React.StrictMode>,
);
